// One place that says which reading aid a learning language uses (LAN-870).
//
// Callers ask this module instead of checking `language === 'japanese' ||
// language === 'chinese'`. It has no heavy imports: the transliteration engine
// loads on demand through loadTransliteration().

// Japanese (furigana/romaji) and Chinese (pinyin) replace the message text
// with segmented ruby markup from their own text services.
const SEGMENTED_READING_AID_LANGUAGES = Object.freeze(['japanese', 'chinese'])

// Greek and Korean keep the word markup and add a romanization under each word.
// Must match the keys of TRANSLITERATION_ENGINES in ../transliteration/index.js
// (a test checks this).
export const TRANSLITERATION_LANGUAGES = Object.freeze(['greek', 'korean'])

export const TRANSLITERATION = 'transliteration'

function normalizeLanguage(language) {
  return String(language || '').toLowerCase()
}

// 'japanese' | 'chinese' | 'transliteration' | null
export function readingAidEngineFor(language) {
  const key = normalizeLanguage(language)
  if (SEGMENTED_READING_AID_LANGUAGES.includes(key)) return key
  if (TRANSLITERATION_LANGUAGES.includes(key)) return TRANSLITERATION
  return null
}

// Japanese and Chinese: text is re-rendered by a text service, and word-level
// karaoke highlighting is off.
export function usesSegmentedReadingAid(language) {
  return SEGMENTED_READING_AID_LANGUAGES.includes(normalizeLanguage(language))
}

export function usesTransliteration(language) {
  return readingAidEngineFor(language) === TRANSLITERATION
}

// LAN-688: 'furigana' is a legacy stored value. The app maps it to 'hiragana';
// before this, the web matched neither and rendered no aid at all.
export function normalizePronunciationCharacters(value) {
  return value === 'furigana' ? 'hiragana' : value
}

// True when this language shows a transliteration for this setting value.
export function transliterationOn(language, value) {
  return usesTransliteration(language) && normalizePronunciationCharacters(value) === TRANSLITERATION
}

let transliterationLoad = null

// The engine and DOM helpers, in their own chunk.
export function loadTransliteration() {
  if (!transliterationLoad) {
    transliterationLoad = import(
      /* webpackChunkName: "transliteration" */
      '../transliteration/dom'
    ).catch(error => {
      transliterationLoad = null
      throw error
    })
  }
  return transliterationLoad
}

// Annotate word elements inside `root` when the aid is on for this language.
// Resolves to the number of annotated words (0 when off or on failure).
export async function annotateTransliteration(root, language, value) {
  if (!root || !transliterationOn(language, value)) return 0
  try {
    const dom = await loadTransliteration()
    return dom.annotateWithin(root, normalizeLanguage(language))
  } catch (error) {
    // Leave the text as it is; never log message text.
    console.warn('Unable to load transliteration reading aids:', error?.message)
    return 0
  }
}

export async function clearTransliteration(root) {
  if (!root || !transliterationLoad) return
  try {
    const dom = await transliterationLoad
    dom.clearWithin(root)
  } catch (_error) {
    // Nothing was annotated if the chunk never loaded.
  }
}
